import { heightAt, inBounds, isSummitSquare, SIZE } from './board.js';
import type { Action, GameState, Piece, PieceAction } from './types.js';

type Dir = readonly [number, number];

const ORTHOGONAL: readonly Dir[] = [
  [-1, 0],
  [1, 0],
  [0, -1],
  [0, 1],
];

const ALL_8: readonly Dir[] = [...ORTHOGONAL, [-1, -1], [-1, 1], [1, -1], [1, 1]];

const KNIGHT: readonly Dir[] = [
  [-2, -1],
  [-2, 1],
  [-1, -2],
  [-1, 2],
  [1, -2],
  [1, 2],
  [2, -1],
  [2, 1],
];

/** Highest a normal step may climb. */
export const MAX_CLIMB = 1;
/** How far the Lookout sees. */
export const LOOKOUT_RANGE = 3;
/** Closest distance at which the Lookout may pin. */
export const MIN_PIN_DISTANCE = 2;

/** Alive pieces indexed by `r * 9 + c`. */
export type Occupancy = (Piece | undefined)[];

export function occupancy(pieces: readonly Piece[]): Occupancy {
  const grid: Occupancy = new Array<Piece | undefined>(SIZE * SIZE).fill(undefined);
  for (const p of pieces) if (p.alive) grid[p.r * SIZE + p.c] = p;
  return grid;
}

export function pieceAt(grid: Occupancy, r: number, c: number): Piece | undefined {
  return grid[r * SIZE + c];
}

/** True while the piece cannot act: on its owner's turn right after it was pinned. */
export function isPinned(state: Pick<GameState, 'ply'>, piece: Pick<Piece, 'pinnedAt'>): boolean {
  return piece.pinnedAt !== undefined && state.ply - piece.pinnedAt === 1;
}

/** A piece pinned at ply p is immune until ply - p > 2. */
export function canBePinned(
  state: Pick<GameState, 'ply'>,
  piece: Pick<Piece, 'pinnedAt'>,
): boolean {
  return piece.pinnedAt === undefined || state.ply - piece.pinnedAt > 2;
}

/** Legal actions of one piece. Empty when the game is over or the piece cannot act now. */
export function legalActions(state: GameState, pieceId: string): Action[] {
  if (state.winReason !== null) return [];
  const piece = state.pieces.find((p) => p.id === pieceId);
  if (piece === undefined || !canAct(state, piece)) return [];
  return actionsOf(state, piece, occupancy(state.pieces));
}

/** Every legal action of the side to move. */
export function allLegalActions(state: GameState): PieceAction[] {
  if (state.winReason !== null) return [];
  const grid = occupancy(state.pieces);
  const result: PieceAction[] = [];
  for (const piece of state.pieces) {
    if (!canAct(state, piece)) continue;
    for (const action of actionsOf(state, piece, grid)) result.push({ pieceId: piece.id, action });
  }
  return result;
}

/** Whether the side to move has at least one legal action. Cheaper than `allLegalActions`. */
export function hasLegalAction(state: GameState): boolean {
  const grid = occupancy(state.pieces);
  return state.pieces.some((p) => canAct(state, p) && actionsOf(state, p, grid).length > 0);
}

function canAct(state: GameState, piece: Piece): boolean {
  return piece.alive && piece.team === state.turn && !isPinned(state, piece);
}

function actionsOf(state: GameState, piece: Piece, grid: Occupancy): Action[] {
  const out: Action[] = [];
  // Opening-build compensation: the very first action is a Mason build, and nothing else.
  if (state.ply === 0 && state.config.secondPlayerCompensation === 'opening-build') {
    if (piece.type === 'M') for (const d of ALL_8) build(state, piece, grid, d, out);
    return out;
  }
  switch (piece.type) {
    case 'B':
      if (state.config.beaconMode === 'escorted' && !escorted(piece, grid)) break;
      for (const d of ALL_8) step(state, piece, grid, d, out);
      break;
    case 'C':
      for (const d of ORTHOGONAL) climberLine(state, piece, grid, d, out);
      break;
    case 'X':
      for (const d of KNIGHT) step(state, piece, grid, d, out);
      break;
    case 'L':
      for (const d of ORTHOGONAL) step(state, piece, grid, d, out);
      if (state.config.lookoutMode === 'pin') {
        for (const d of ALL_8) pinLine(state, piece, grid, d, out);
      }
      break;
    case 'M':
      for (const d of ALL_8) step(state, piece, grid, d, out);
      for (const d of ALL_8) build(state, piece, grid, d, out);
      break;
  }
  return out;
}

/** One step (or one knight jump) obeying the cliff rule: at most +1, any amount down. */
function step(state: GameState, piece: Piece, grid: Occupancy, [dr, dc]: Dir, out: Action[]): void {
  const r = piece.r + dr;
  const c = piece.c + dc;
  if (!inBounds(r, c)) return;
  if (heightAt(state.heights, r, c) - heightAt(state.heights, piece.r, piece.c) > MAX_CLIMB) return;
  arrive(state, piece, grid, r, c, out);
}

/** A friendly piece stands on one of the 8 squares around this piece. */
function escorted(piece: Piece, grid: Occupancy): boolean {
  return ALL_8.some(([dr, dc]) => {
    const r = piece.r + dr;
    const c = piece.c + dc;
    return inBounds(r, c) && pieceAt(grid, r, c)?.team === piece.team;
  });
}

function arrive(
  state: GameState,
  piece: Piece,
  grid: Occupancy,
  r: number,
  c: number,
  out: Action[],
): void {
  const target = pieceAt(grid, r, c);
  if (target === undefined) out.push({ kind: 'move', r, c });
  // An escorted Beacon cannot be captured: its square simply blocks the way.
  else if (target.type === 'B' && state.config.beaconMode === 'escorted') return;
  else if (target.team !== piece.team) out.push({ kind: 'capture', r, c, target: target.id });
}

/**
 * Climber: 1 or 2 orthogonal squares. Each step may climb +1; a +2 step is allowed only as the
 * first step and ends the move; +3 or more is impossible. Stops at the first occupied square.
 */
function climberLine(
  state: GameState,
  piece: Piece,
  grid: Occupancy,
  [dr, dc]: Dir,
  out: Action[],
): void {
  let fromHeight = heightAt(state.heights, piece.r, piece.c);
  for (let distance = 1; distance <= 2; distance++) {
    const r = piece.r + dr * distance;
    const c = piece.c + dc * distance;
    if (!inBounds(r, c)) return;
    const height = heightAt(state.heights, r, c);
    const rise = height - fromHeight;
    if (rise >= 3) return;
    if (rise === 2 && distance > 1) return;
    if (pieceAt(grid, r, c) !== undefined) {
      arrive(state, piece, grid, r, c, out);
      return;
    }
    out.push({ kind: 'move', r, c });
    if (rise === 2) return;
    fromHeight = height;
  }
}

/**
 * Lookout pin: look up to 3 squares. Sight stops at the first piece or at the first square
 * higher than the Lookout's own square (a piece standing on that higher square is not seen).
 * The first piece seen can be pinned if it is an enemy at distance 2 or 3 and not immune.
 */
function pinLine(
  state: GameState,
  piece: Piece,
  grid: Occupancy,
  [dr, dc]: Dir,
  out: Action[],
): void {
  const eyeHeight = heightAt(state.heights, piece.r, piece.c);
  for (let distance = 1; distance <= LOOKOUT_RANGE; distance++) {
    const r = piece.r + dr * distance;
    const c = piece.c + dc * distance;
    if (!inBounds(r, c) || heightAt(state.heights, r, c) > eyeHeight) return;
    const seen = pieceAt(grid, r, c);
    if (seen === undefined) continue;
    if (seen.team !== piece.team && distance >= MIN_PIN_DISTANCE && canBePinned(state, seen)) {
      out.push({ kind: 'pin', r, c, pin: seen.id });
    }
    return;
  }
}

/** Mason: raise or lower an empty neighbouring square by 1, within 0–maxHeight, never the summit. */
function build(
  state: GameState,
  piece: Piece,
  grid: Occupancy,
  [dr, dc]: Dir,
  out: Action[],
): void {
  const r = piece.r + dr;
  const c = piece.c + dc;
  if (!inBounds(r, c) || isSummitSquare(state.config.summitMode, r, c)) return;
  if (pieceAt(grid, r, c) !== undefined) return;
  const height = heightAt(state.heights, r, c);
  const top = state.config.maxHeight;
  const maxBuild = state.config.masonMayBuildTo4 ? top : top - 1;
  if (height + 1 <= maxBuild) out.push({ kind: 'build', r, c, delta: 1 });
  if (height - 1 >= 0) out.push({ kind: 'build', r, c, delta: -1 });
}

export function sameAction(a: Action, b: Action): boolean {
  if (a.kind !== b.kind || a.r !== b.r || a.c !== b.c) return false;
  switch (a.kind) {
    case 'move':
      return true;
    case 'capture':
      return b.kind === 'capture' && a.target === b.target;
    case 'pin':
      return b.kind === 'pin' && a.pin === b.pin;
    case 'build':
      return b.kind === 'build' && a.delta === b.delta;
  }
}
